/**
 * Number formatting for the metrics screens.
 *
 * Separate from `src/format.ts`, which is about instants and calendar dates.
 * Nothing here touches a timezone: the server has already decided which
 * local day a row belongs to (invariant 41), so a metric date is a label,
 * never something to convert.
 */

/** 1_234_567 -> "1.23M". Compact, because a KPI tile is read at a glance. */
export const compact = (n: number): string =>
  new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 2 }).format(n);

/** Full precision with separators, for tables and tooltips. */
export const full = (n: number): string => new Intl.NumberFormat().format(n);

/**
 * A rate as a percentage. `null` is rendered as an em dash, never 0%.
 *
 * The distinction is load-bearing: a campaign that placed no calls has no
 * connect rate, which is a different statement from a connect rate of zero.
 * Printing "0%" for a quiet day reads as a catastrophic one.
 */
export const pct = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined ? "—" : `${(v * 100).toFixed(digits)}%`;

/** Seconds as "m:ss", or an em dash when there is nothing to average. */
export const duration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** "2026-09-21" -> "21 Sep". The string is already a calendar date. */
export const shortDate = (iso: string): string => {
  const [, m, d] = iso.split("-").map(Number);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d} ${months[m - 1]}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** "2026-09-24" -> "Thu 24 Sep 2026", for a tooltip that must stand alone. */
export const longDate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  const wd = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return `${WEEKDAYS[wd]} ${d} ${MONTHS[m - 1]} ${y}`;
};

/** "Sep", or "Sep 2025" when it is not the given year. */
export const monthName = (iso: string, currentYear?: number): string => {
  const [y, m] = iso.split("-").map(Number);
  return currentYear === undefined || y === currentYear ? MONTHS[m - 1] : `${MONTHS[m - 1]} ${y}`;
};

/** "14–20 Sep", or "28 Sep – 4 Oct" across a month boundary. */
export const dateRange = (from: string, to: string): string => {
  const [, fm, fd] = from.split("-").map(Number);
  const [, tm, td] = to.split("-").map(Number);
  if (from === to) return `${fd} ${MONTHS[fm - 1]}`;
  return fm === tm ? `${fd}–${td} ${MONTHS[tm - 1]}` : `${fd} ${MONTHS[fm - 1]} – ${td} ${MONTHS[tm - 1]}`;
};
